"use client";

import { EmbeddedCheckoutProvider } from "@repo/stripe/context/EmbeddedCheckoutContext";
import { Drawer } from "vaul";

import { usePricingPlanKeys } from "src/lib/pricing-plan-keys";
import PRODUCT_NAME from "src/lib/product-name";
import {
  StripeCheckoutContent,
  type StripeThemeConfig,
} from "src/lib/stripe-checkout";
import type { PaymentModalContentProps } from "../types";

// Dark theme configuration for the modal checkout
const modalTheme: StripeThemeConfig = {
  appearance: {
    theme: "flat",
    variables: {
      colorPrimary: "#00a1c5",
      colorBackground: "#001e2b",
      colorText: "#ffffff",
      colorDanger: "#df1b41",
      borderRadius: "12px",
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
    },
  },
  fonts: [
    {
      cssSrc:
        "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap",
    },
  ],
  clientTypography: {
    fontSizeBase: "16px",
    fontWeightNormal: "400",
    fontWeightMedium: "500",
    fontWeightBold: "600",
  },
  submitFooterClassName: "mt-6 w-full px-[24px]",
  submitButtonClassName: "rounded-lg",
  summaryCardTheme: {
    // TODO: this modal SummaryCard was never wired correctly — backgroundColor
    // was a raw hex value (rendered as a className) and required fields were
    // missing. Filled in with placeholders to satisfy the type; revisit when
    // the modal flow gets active use.
    backgroundColor: "bg-slate-900",
    activeButtonColor: "text-cyan-500",
    dividerColor: "rgba(255,255,255,0.1)",
  },
};

/**
 * Payment modal content with Stripe Elements checkout.
 * Wraps the checkout form with the EmbeddedCheckoutProvider.
 */
export function PaymentModalContent({
  productKey,
  cadence,
  onBack,
  onPaymentComplete,
}: PaymentModalContentProps) {
  // Get pricing plan keys based on product
  const pricingPlanKeys = usePricingPlanKeys();

  const pricingKey = productKey;
  const selectedPlanKeys =
    pricingPlanKeys[pricingKey] || pricingPlanKeys["unlimitedRx"];

  // Trial length comes from Stripe Price metadata.trialDays — no override.

  if (!selectedPlanKeys) {
    return (
      <div className="flex flex-col items-center justify-center p-[24px] bg-teal-1000">
        <p className="text-white">Loading payment...</p>
      </div>
    );
  }

  return (
    <EmbeddedCheckoutProvider
      pricingPlanKeys={selectedPlanKeys}
      productName={PRODUCT_NAME}
      defaultCadence={cadence}
    >
      <div className="flex flex-col w-full bg-teal-1000">
        <Drawer.Title className="sr-only">Complete your payment</Drawer.Title>
        <Drawer.Description className="sr-only">
          Enter your payment details to complete your subscription
        </Drawer.Description>

        <StripeCheckoutContent
          productKey={pricingKey}
          theme={modalTheme}
          onPaymentComplete={onPaymentComplete}
          onBack={onBack}
          backLabel="Back to plans"
          source="subscription_gate"
          additionalMetadata={{ source: "subscription_gate" }}
        />
      </div>
    </EmbeddedCheckoutProvider>
  );
}
